import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Radar, Play, Pause, Trash2, Send, CheckCircle, XCircle,
  Clock, Terminal, Zap, Search, AlertTriangle
} from "lucide-react";

const DEFAULT_PORTS = [22, 80, 443, 3000, 3500, 4030, 4032, 4035, 4070, 5000, 5555, 6754, 7070, 8080, 8443, 8888, 9090, 20564, 25956];

const DEFAULT_PATHS = [
  "/", "/api", "/api/", "/api/1", "/api/1/vehicles", "/api/diagnostics",
  "/status", "/version", "/info", "/health", "/debug", "/service",
  "/command", "/firmware", "/update", "/config", "/certs",
  "/_odin_routine_request_", "/_data_get_value_request_", "/_data_set_value_request_",
  "/readsig", "/handshake", "/install", "/reset", "/service-redeploy",
  "/v1/api", "/v2/status", "/v2/service", "/hermes", "/ws", "/toolbox",
  "/Tesla", "/diag", "/vehicles", "/favicon.ico", "/index.html",
  "/api/1/vehicle_data", "/api/vehicle", "/api/1/vehicles/5YJSA1CNXDFP16107",
];

export default function MCUScanner() {
  const [scanning, setScanning] = useState(false);
  const [portResults, setPortResults] = useState({});
  const [probeResults, setProbeResults] = useState([]);
  const [customPath, setCustomPath] = useState("");
  const [customPort, setCustomPort] = useState("8080");
  const [probeMethod, setProbeMethod] = useState("GET");
  const [log, setLog] = useState([]);
  const logRef = useRef(null);

  const addLog = useCallback((msg, type = "info") => {
    setLog(prev => [...prev.slice(-200), { ts: new Date().toLocaleTimeString(), msg, type }]);
  }, []);

  const scanPorts = useCallback(async () => {
    setScanning(true);
    addLog("Starting port scan on 192.168.90.100...", "cmd");
    try {
      const resp = await axios.post(`${API}/mcu/scan`, { ports: DEFAULT_PORTS });
      setPortResults(resp.data.ports);
      const openPorts = Object.entries(resp.data.ports).filter(([_, v]) => v.open).map(([p]) => p);
      addLog(`Scan complete: ${openPorts.length} open ports: ${openPorts.join(", ") || "none"}`, openPorts.length > 0 ? "ok" : "err");
      if (openPorts.length > 0) toast.success(`Found ${openPorts.length} open ports`);
    } catch (e) {
      addLog("Port scan failed: " + (e.response?.data?.detail || e.message), "err");
    }
    setScanning(false);
  }, [addLog]);

  const probePaths = useCallback(async (method) => {
    setScanning(true);
    const port = parseInt(customPort) || 8080;
    addLog(`Probing ${DEFAULT_PATHS.length} paths on port ${port} (${method})...`, "cmd");
    try {
      const resp = await axios.post(`${API}/mcu/scan`, {
        paths: DEFAULT_PATHS,
        port: port,
        method: method
      });
      setProbeResults(resp.data.probes);
      const nonFour = resp.data.probes.filter(p => p.status !== 404 && p.status !== "timeout" && p.status !== "refused");
      addLog(`Probe complete: ${resp.data.probes.length} paths tested, ${nonFour.length} non-404 responses`, nonFour.length > 0 ? "ok" : "info");
      if (nonFour.length > 0) {
        toast.success(`Found ${nonFour.length} non-404 paths!`);
        nonFour.forEach(p => addLog(`HIT: ${p.path} -> ${p.status} ${p.body?.slice(0, 100) || ""}`, "ok"));
      }
    } catch (e) {
      addLog("Path probe failed: " + (e.response?.data?.detail || e.message), "err");
    }
    setScanning(false);
  }, [customPort, addLog]);

  const probeCustom = useCallback(async () => {
    if (!customPath) return;
    const port = parseInt(customPort) || 8080;
    const path = customPath.startsWith("/") ? customPath : "/" + customPath;
    addLog(`${probeMethod} http://192.168.90.100:${port}${path}`, "cmd");
    try {
      const resp = await axios.post(`${API}/mcu/scan`, {
        paths: [path],
        port: port,
        method: probeMethod
      });
      const r = resp.data.probes[0];
      if (r) {
        const isHit = r.status !== 404 && r.status !== "timeout" && r.status !== "refused";
        addLog(`${r.status} ${r.body?.slice(0, 300) || ""}`, isHit ? "ok" : "info");
        if (r.headers) addLog(`Headers: ${JSON.stringify(r.headers).slice(0, 300)}`, "info");
        setProbeResults(prev => [r, ...prev]);
      }
    } catch (e) {
      addLog("Probe failed: " + e.message, "err");
    }
  }, [customPath, customPort, probeMethod, addLog]);

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1600, margin: "0 auto" }}>
      <style>{`
        .scard { background:#0e0e16; border:1px solid #1a1a28; border-radius:10px; padding:20px; }
        .stitle { font-size:11px; text-transform:uppercase; letter-spacing:2px; color:#6b7280; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
        .sbtn { font-size:11px; padding:7px 14px; border-radius:5px; border:1px solid #1e1e2e; background:#14141e; color:#e2e8f0; cursor:pointer; font-weight:600; letter-spacing:.5px; transition:all .15s; display:inline-flex; align-items:center; gap:6px; }
        .sbtn:hover { background:#1c1c2a; border-color:#3a3a4e; }
        .sbtn:disabled { opacity:0.4; cursor:not-allowed; }
        .sbtn-g { border-color:rgba(45,212,168,.3); color:#2dd4a8; }
        .sbtn-b { border-color:rgba(74,158,255,.3); color:#4a9eff; }
        .sbtn-r { border-color:rgba(232,64,87,.3); color:#e84057; }
        .sinput { font-size:12px; padding:7px 12px; border-radius:5px; border:1px solid #1e1e2e; background:#0a0a10; color:#e2e8f0; outline:none; }
        .sinput:focus { border-color:#4a9eff; }
        .slog { background:#08080d; border:1px solid #1a1a28; border-radius:6px; padding:10px; font-size:11px; max-height:250px; overflow-y:auto; line-height:1.7; font-family:monospace; }
        .slog .ok { color:#2dd4a8; } .slog .err { color:#e84057; } .slog .cmd { color:#4a9eff; } .slog .info { color:#6b7280; }
        .port-chip { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border-radius:5px; font-size:11px; font-weight:600; margin:3px; }
        .port-open { background:rgba(45,212,168,0.1); border:1px solid rgba(45,212,168,0.3); color:#2dd4a8; }
        .port-closed { background:rgba(255,255,255,0.03); border:1px solid #1a1a28; color:#6b7280; }
        .probe-row { display:flex; align-items:center; gap:10px; padding:6px 10px; border-radius:5px; font-size:12px; }
        .probe-row:hover { background:rgba(255,255,255,0.02); }
      `}</style>

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#e2e8f0", letterSpacing: 1, marginBottom: 4 }}>MCU Port Scanner</h1>
        <p style={{ fontSize: 12, color: "#6b7280" }}>
          Probe Tesla MCU at 192.168.90.100 - VIN: 5YJSA1CNXDFP16107
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Port Scanner */}
        <div className="scard" data-testid="port-scanner-card">
          <div className="stitle"><Radar size={14} color="#4a9eff" /> Port Scanner</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button className="sbtn sbtn-b" onClick={scanPorts} disabled={scanning} data-testid="scan-ports-btn">
              {scanning ? <Clock size={12} /> : <Radar size={12} />}
              {scanning ? "Scanning..." : "Scan All Ports"}
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {DEFAULT_PORTS.map(p => {
              const r = portResults[p];
              const isOpen = r?.open;
              return (
                <div key={p} className={`port-chip ${isOpen ? "port-open" : "port-closed"}`}>
                  {isOpen ? <CheckCircle size={10} /> : <XCircle size={10} />}
                  {p}
                </div>
              );
            })}
          </div>
          {Object.entries(portResults).filter(([_, v]) => v.open && v.banner).map(([port, v]) => (
            <div key={port} style={{ fontSize: 10, color: "#2dd4a8", marginTop: 6, fontFamily: "monospace" }}>
              Port {port} banner: {v.banner}
            </div>
          ))}
        </div>

        {/* Custom Probe */}
        <div className="scard" data-testid="custom-probe-card">
          <div className="stitle"><Search size={14} color="#f5a623" /> Custom Probe</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center" }}>
            <select className="sinput" value={probeMethod} onChange={e => setProbeMethod(e.target.value)} style={{ width: 80 }} data-testid="probe-method">
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="OPTIONS">OPTIONS</option>
            </select>
            <span style={{ color: "#6b7280", fontSize: 11 }}>:</span>
            <input className="sinput" style={{ width: 60 }} value={customPort} onChange={e => setCustomPort(e.target.value)} placeholder="8080" data-testid="probe-port" />
            <input className="sinput" style={{ flex: 1 }} value={customPath} onChange={e => setCustomPath(e.target.value)} placeholder="/path/to/probe" onKeyDown={e => e.key === "Enter" && probeCustom()} data-testid="probe-path" />
            <button className="sbtn sbtn-g" onClick={probeCustom} disabled={scanning} data-testid="probe-send-btn">
              <Send size={11} /> Probe
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            <button className="sbtn sbtn-b" onClick={() => probePaths("GET")} disabled={scanning} data-testid="probe-all-get">
              <Zap size={11} /> Probe All (GET)
            </button>
            <button className="sbtn sbtn-b" onClick={() => probePaths("POST")} disabled={scanning}>
              <Zap size={11} /> Probe All (POST)
            </button>
            <button className="sbtn" onClick={() => { setProbeResults([]); setLog([]); }} data-testid="clear-results">
              <Trash2 size={11} /> Clear
            </button>
          </div>
          <div style={{ fontSize: 10, color: "#6b7280" }}>
            Quick: {["/", "/api", "/status", "/_odin_routine_request_", "/diag"].map(p => (
              <span key={p} onClick={() => { setCustomPath(p); }} style={{ cursor: "pointer", color: "#4a9eff", marginRight: 8 }}>{p}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Probe Results */}
      {probeResults.length > 0 && (
        <div className="scard" style={{ marginBottom: 16 }} data-testid="probe-results-card">
          <div className="stitle"><Terminal size={14} color="#2dd4a8" /> Probe Results ({probeResults.length} paths)</div>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {probeResults.map((r, i) => {
              const isHit = r.status !== 404 && r.status !== "timeout" && r.status !== "refused" && r.status !== "error";
              return (
                <div key={i} className="probe-row" style={{ background: isHit ? "rgba(45,212,168,0.05)" : "transparent" }}>
                  <span style={{
                    fontFamily: "monospace", fontSize: 11, fontWeight: 700, minWidth: 36,
                    color: r.status === 200 ? "#2dd4a8" : r.status === 404 ? "#6b7280" : r.status === 405 ? "#f5a623" : "#e84057"
                  }}>
                    {r.status}
                  </span>
                  <span style={{ fontSize: 10, color: "#4a9eff", minWidth: 50 }}>{r.method || "GET"}</span>
                  <span style={{ fontSize: 11, color: isHit ? "#e2e8f0" : "#6b7280", flex: 1, fontFamily: "monospace" }}>
                    :{r.port}{r.path}
                  </span>
                  {r.body && r.body !== "404: Not Found" && (
                    <span style={{ fontSize: 10, color: "#2dd4a8", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.body.slice(0, 100)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Log */}
      <div className="scard" data-testid="scanner-log-card">
        <div className="stitle"><Terminal size={14} color="#4a9eff" /> Scanner Log</div>
        <div className="slog" ref={logRef}>
          {log.length === 0 && <div className="info">[ READY ] Scan ports or probe paths to begin...</div>}
          {log.map((l, i) => <div key={i} className={l.type}>[{l.ts}] {l.msg}</div>)}
        </div>
      </div>
    </div>
  );
}
